import { Button, EmptyState, Select, Spinner } from '@bennows/boows-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StatChart } from '../../../../web/src/components/charts/StatChart';
import { formatDayShort } from '../../lib/format';
import { TREND_METRICS, TREND_WINDOWS, useTenantTrend, type TrendMetric } from './useTenantUsage';

/** One figure of the tenant per day from the daily snapshots, as a line (REQ-140-016). */
export function TrendCard({ tenantId }: { readonly tenantId: string }) {
  const { t } = useTranslation();
  const [metric, setMetric] = useState<TrendMetric>('ai_credits_month');
  const [days, setDays] = useState<number>(30);
  const trend = useTenantTrend(tenantId, metric, days);
  const name = t(`trend.metrics.${metric}`);
  const points = trend.data?.points ?? [];

  let body;
  if (trend.isPending) {
    body = (
      <div className="ui-console-loading" role="status">
        <Spinner label={t('common.loading')} />
      </div>
    );
  } else if (trend.isError) {
    body = (
      <div className="ui-error-state" role="alert">
        <p className="ui-error-state__text">{t('trend.loadError')}</p>
        <Button variant="secondary" onClick={() => void trend.refetch()}>
          {t('common.retry')}
        </Button>
      </div>
    );
  } else if (points.every((point) => point.value === null)) {
    body = <EmptyState title={t('trend.empty')} />;
  } else {
    body = (
      <StatChart
        title={t('trend.title', { metric: name })}
        kind="line"
        categories={points.map((point) => ({ key: point.day, label: formatDayShort(point.day) }))}
        series={[{ key: metric, label: t('trend.series') }]}
        value={(day) => points.find((point) => point.day === day)?.value ?? null}
        tableCaption={t('trend.caption', { metric: name })}
      />
    );
  }

  return (
    <div className="ui-stack">
      <div className="ui-console-toolbar">
        <Select
          hideLabel
          label={t('trend.metric')}
          value={metric}
          options={TREND_METRICS.map((id) => ({ id, label: t(`trend.metrics.${id}`) }))}
          onChange={(id) => setMetric(id as TrendMetric)}
        />
        <Select
          hideLabel
          label={t('trend.window')}
          value={String(days)}
          options={TREND_WINDOWS.map((count) => ({ id: String(count), label: t('trend.windowOption', { days: count }) }))}
          onChange={(id) => setDays(Number(id))}
        />
      </div>
      {body}
    </div>
  );
}
